/**
 * Reduces authorized ProposalJSON collection entries to the semantic values
 * permitted in the negotiation ledger. Display, permission, lifecycle, and
 * snapshot metadata never crosses this boundary.
 */
export function canonicalProposalCollectionItem(path: string, item: unknown): unknown {
  if (path.endsWith("TemplateIds")) return canonicalUuidValue(item);
  if (!isRecord(item)) return clone(item);

  if (path.endsWith("/documents")) {
    const canonical: Record<string, unknown> = {};
    const documentId =
      ownValue(item, "documentId") ??
      (isRecord(item.documentInfo) ? ownValue(item.documentInfo, "id") : undefined) ??
      ownValue(item, "id");
    if (documentId !== undefined) canonical.documentId = canonicalUuidValue(documentId);
    copyOwnUuidProperties(item, canonical, ["replacesId"]);
    return canonical;
  }

  if (path.endsWith("/members")) {
    const canonical: Record<string, unknown> = {};
    copyOwnUuidProperties(item, canonical, ["userId", "legalEntityId"]);
    return canonical;
  }

  if (path === "/feeSchedule/categories") {
    const canonical: Record<string, unknown> = {};
    copyOwnUuidProperties(item, canonical, ["id"]);
    copyOwnProperties(item, canonical, [
      "name",
      "description",
      "billingCode",
      "feeRate",
      "feeCurrency",
      "feeUnit",
      "teamMember",
    ]);
    return canonical;
  }

  return clone(item);
}

function copyOwnUuidProperties(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  properties: readonly string[],
): void {
  for (const property of properties) {
    if (Object.prototype.hasOwnProperty.call(source, property) && source[property] !== undefined) {
      target[property] = canonicalUuidValue(source[property]);
    }
  }
}

function canonicalUuidValue(value: unknown): unknown {
  return getProposalCollectionItemIdentity(value) ?? clone(value);
}

/**
 * Produces the canonical collection value carried by a content command.
 *
 * Fee-category domain serialization deliberately writes optional editable
 * properties with `undefined` when a user clears them. JSON would otherwise
 * erase that intent, so command construction turns only those own properties
 * into explicit nulls. A genuinely omitted property remains omitted and is
 * still safe for sparse/older projections.
 */
export function canonicalProposalCollectionItemForCommand(path: string, item: unknown): unknown {
  const canonical = canonicalProposalCollectionItem(path, item);
  if (path !== "/feeSchedule/categories" || !isRecord(item) || !isRecord(canonical)) return canonical;

  for (const property of ["description", "billingCode", "feeUnit", "teamMember"] as const) {
    if (Object.prototype.hasOwnProperty.call(item, property) && item[property] === undefined) {
      canonical[property] = null;
    }
  }
  return canonical;
}

export function proposalSemanticJsonValuesEqual(left: unknown, right: unknown): boolean {
  if ((left === null || left === undefined) && (right === null || right === undefined)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => proposalSemanticJsonValuesEqual(value, right[index]))
    );
  }
  if (isRecord(left) || isRecord(right)) {
    if (!isRecord(left) || !isRecord(right)) return false;
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    return [...keys].every((key) => proposalSemanticJsonValuesEqual(left[key], right[key]));
  }
  return Object.is(left, right);
}

/**
 * The strict sibling of `proposalSemanticJsonValuesEqual`: `null` and `undefined` are distinct,
 * everywhere, including nested. Deliberately not the same function with a flag — the two encode
 * different domain decisions and reusing one implementation for both keeps that decision explicit at
 * every call site rather than hidden behind a boolean.
 *
 * Correct for callers diffing a value against its own prior value to decide whether an edit is a net
 * no-op (`proposal-command-builder`'s content-operation diff, `proposal-collaboration-state`'s pending-
 * operation composition): most scalar fields distinguish "explicitly cleared" (`null`) from "never
 * set"/"round-tripped through an optional editor field" (`undefined`), and only a short, explicit list
 * of paths (`nullableAbsentFieldPaths` in `proposal-command-builder`) coalesces the two before this
 * function ever sees them. `proposalSemanticJsonValuesEqual` is instead correct where a queued value is
 * compared against server-supplied resolution/baseline state, which never distinguishes the two.
 */
export function proposalStrictJsonValuesEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => proposalStrictJsonValuesEqual(value, right[index]))
    );
  }
  if (isRecord(left) || isRecord(right)) {
    if (!isRecord(left) || !isRecord(right)) return false;
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    return [...keys].every((key) => proposalStrictJsonValuesEqual(left[key], right[key]));
  }
  return Object.is(left, right);
}

function copyOwnProperties(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  properties: readonly string[],
): void {
  for (const property of properties) {
    if (Object.prototype.hasOwnProperty.call(source, property) && source[property] !== undefined) {
      target[property] = clone(source[property]);
    }
  }
}

function ownValue(source: Record<string, unknown>, property: string): unknown {
  return Object.prototype.hasOwnProperty.call(source, property) ? source[property] : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function clone<T>(value: T): T {
  return value === undefined ? value : structuredClone(value);
}
import { getProposalCollectionItemIdentity } from "work/proposals/negotiation/proposal-operation-identity";
